import React, { useMemo, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Transition } from '@headlessui/react';
import { GlobeAltIcon, CheckIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import ConfigurationService from '../../services/ConfigurationService';

// ============================================================================
// Types
// ============================================================================

interface LanguageOption {
    code: string;
    name: string;
    nativeName: string;
}

interface LanguageSwitcherProps {
    /** Controlled open state managed by the parent Header */
    isOpen: boolean;
    /** Callback to toggle this dropdown (parent handles mutual exclusion) */
    onToggle: () => void;
}

// ============================================================================
// Language Metadata Registry
// ============================================================================

const LANGUAGE_METADATA: Record<string, Omit<LanguageOption, 'code'>> = {
    en: { name: 'English',     nativeName: 'English'       },
    am: { name: 'Amharic',     nativeName: 'አማርኛ'         },
    om: { name: 'Afan Oromo',  nativeName: 'Afaan Oromoo'  },
    so: { name: 'Somali',      nativeName: 'Soomaali'      },
    ti: { name: 'Tigrinya',    nativeName: 'ትግርኛ'         },
};

// ============================================================================
// Component
// ============================================================================

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ isOpen, onToggle }) => {
    const { i18n, t } = useTranslation();

    const supportedLanguages = useMemo<LanguageOption[]>(() => {
        const codes = ConfigurationService.getSupportedLanguages();
        return codes
            .filter((code) => LANGUAGE_METADATA[code])
            .map((code) => ({ code, ...LANGUAGE_METADATA[code] }));
    }, []);

    const changeLanguage = (lng: string) => {
        if (i18n.language !== lng) {
            i18n.changeLanguage(lng);
        }
        // Close after selection
        if (isOpen) onToggle();
    };

    const isCurrentLanguage = (langCode: string): boolean =>
        i18n.language === langCode || i18n.language?.startsWith(`${langCode}-`);

    const currentLanguage = useMemo(() => {
        const code = i18n.language?.split('-')[0] || 'en';
        return { code, ...(LANGUAGE_METADATA[code] || LANGUAGE_METADATA.en) };
    }, [i18n.language]);

    return (
        <div className="relative inline-block text-left">
            {/* ── Trigger Button ─────────────────────────────────────── */}
            <button
                id="language-switcher-button"
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                onClick={onToggle}
                className={`group inline-flex items-center justify-center gap-x-1.5 rounded-full px-3 py-1.5 text-sm font-semibold shadow-sm ring-1 ring-inset transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-theme-primary
                    ${isOpen
                        ? 'bg-theme-primary/10 text-theme-primary ring-theme-primary/40'
                        : 'text-theme-sidebar-text ring-theme-divider hover:bg-theme-primary/10 hover:text-theme-primary'
                    }`}
            >
                <GlobeAltIcon
                    className={`-ml-0.5 h-5 w-5 transition-colors duration-300 ${isOpen ? 'text-theme-primary' : 'text-theme-sidebar-icon group-hover:text-theme-primary'}`}
                    aria-hidden="true"
                />
                <span className="hidden sm:inline-block truncate max-w-[80px]">
                    {currentLanguage.nativeName}
                </span>
                <ChevronDownIcon
                    className={`-mr-1 h-4 w-4 transition-all duration-300 ${isOpen ? 'rotate-180 text-theme-primary opacity-100' : 'text-theme-sidebar-icon opacity-60 group-hover:opacity-100'}`}
                    aria-hidden="true"
                />
            </button>

            {/* ── Dropdown Panel ─────────────────────────────────────── */}
            <Transition
                as={Fragment}
                show={isOpen}
                enter="transition ease-out duration-300"
                enterFrom="transform opacity-0 -translate-y-2 scale-95"
                enterTo="transform opacity-100 translate-y-0 scale-100"
                leave="transition ease-in duration-200"
                leaveFrom="transform opacity-100 translate-y-0 scale-100"
                leaveTo="transform opacity-0 -translate-y-2 scale-95"
            >
                <div
                    role="listbox"
                    aria-label={t('common.selectLanguage')}
                    className="absolute right-0 z-50 mt-3 w-64 origin-top-right rounded-xl bg-[rgb(var(--color-stats-container-bg-rgb))]/95 backdrop-blur-md shadow-2xl overflow-hidden border border-theme-divider"
                >
                    {/* Panel header */}
                    <div className="px-4 py-2.5 text-xs font-bold text-theme-text-primary uppercase tracking-wider border-b border-theme-divider">
                        {t('common.selectLanguage')}
                    </div>

                    {/* Language options */}
                    <div className="py-1">
                        {supportedLanguages.map((lang) => {
                            const isCurrent = isCurrentLanguage(lang.code);
                            return (
                                <button
                                    key={lang.code}
                                    role="option"
                                    aria-selected={isCurrent}
                                    onClick={() => changeLanguage(lang.code)}
                                    className={`group flex w-full items-center justify-between px-4 py-3 text-sm transition-all duration-150
                                        ${isCurrent
                                            ? 'bg-theme-primary/10 text-theme-primary font-bold'
                                            : 'text-theme-text-primary font-medium hover:bg-theme-primary/10 hover:text-theme-primary'
                                        }`}
                                >
                                    <div className="flex items-center gap-3">
                                        {/* Active language indicator dot */}
                                        <span
                                            className={`h-1.5 w-1.5 rounded-full transition-all duration-300 ${isCurrent ? 'bg-theme-primary scale-125' : 'bg-theme-divider group-hover:bg-theme-primary/50'}`}
                                        />
                                        <div className="flex flex-col items-start">
                                            <span>{lang.nativeName}</span>
                                            <span className="text-xs opacity-60 font-normal">{lang.name}</span>
                                        </div>
                                    </div>
                                    {isCurrent && (
                                        <CheckIcon className="h-4 w-4 text-theme-primary" aria-hidden="true" />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </Transition>
        </div>
    );
};

export default LanguageSwitcher;
